import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Device, Severity } from '@/lib/postSimulator';
import { LINKEDIN_MAX_CHARS, analyze, reportText } from '@/lib/postSimulator';

const SEVERITY_LABEL: Record<Severity, string> = {
  problem: 'À corriger',
  warning: 'À surveiller',
  ok: 'OK',
};

const SAMPLE = `Nous avons arrêté de poster tous les jours et notre portée a doublé en trois mois, voici ce que nous avons changé dans notre façon d'écrire.

Avant, on publiait pour publier. Un post par jour, quel que soit le sujet.

Maintenant, un post par semaine, mais écrit pour être lu jusqu'au bout.

Le détail des trois changements : https://exemple.fr/article

#linkedin #contenu #marketing`;

export function PostSimulator() {
  const [text, setText] = useState('');
  const [device, setDevice] = useState<Device>('mobile');
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => (text.trim() ? analyze(text) : null), [text]);

  const copyReport = () => {
    if (!result) return;
    try {
      navigator.clipboard
        .writeText(reportText(result.findings))
        .then(() => setCopied(true))
        .catch(() => {
          // Écriture refusée par le navigateur : le bouton reste silencieusement inerte.
        });
    } catch {
      // Pas de Clipboard API (navigateur ancien, contexte non sécurisé) : on n'interrompt rien.
    }
  };

  const onChange = (value: string) => {
    setText(value);
    setCopied(false);
  };

  const cut = result?.cuts[device];
  const problems = result ? result.findings.filter((f) => f.severity === 'problem').length : 0;
  const warnings = result ? result.findings.filter((f) => f.severity === 'warning').length : 0;

  return (
    <div className="nz-sim">
      <label className="nz-sim__label" htmlFor="nz-sim-input">
        Collez votre brouillon
      </label>
      <textarea
        id="nz-sim-input"
        className="nz-sim__input"
        rows={9}
        value={text}
        placeholder="Collez ici le texte de votre post, tel que vous le publieriez."
        onChange={(event) => onChange(event.target.value)}
      />
      <div className="nz-sim__bar">
        <span className={`nz-sim__count${result && result.stats.chars > LINKEDIN_MAX_CHARS ? ' nz-sim__count--over' : ''}`}>
          {result ? result.stats.chars : 0} / {LINKEDIN_MAX_CHARS} caractères
        </span>
        <span className="nz-sim__count">
          {result ? `${result.stats.lines} lignes · ${result.stats.hashtags} hashtag${result.stats.hashtags > 1 ? 's' : ''}` : ''}
        </span>
        <button type="button" className="nz-wizard__back nz-sim__sample" onClick={() => onChange(text ? '' : SAMPLE)}>
          {text ? 'Effacer' : 'Essayer avec un exemple'}
        </button>
      </div>

      {result && cut ? (
        <div className="nz-sim__result" aria-live="polite">
          <div className="nz-sim__tabs" role="group" aria-label="Appareil simulé">
            {(['mobile', 'desktop'] as const).map((d) => (
              <button
                key={d}
                type="button"
                className={`nz-sim__tab${device === d ? ' nz-sim__tab--on' : ''}`}
                aria-pressed={device === d}
                onClick={() => setDevice(d)}
              >
                {d === 'mobile' ? 'Mobile' : 'Ordinateur'}
              </button>
            ))}
          </div>

          <div className={`nz-sim__feed nz-sim__feed--${device}`}>
            <div className="nz-sim__author">
              <span className="nz-sim__avatar" aria-hidden="true" />
              <span>
                <strong>Votre nom</strong>
                <small>Votre titre · maintenant</small>
              </span>
            </div>
            <p className="nz-sim__text">
              {cut.visible}
              {cut.truncated ? <span className="nz-sim__more">{' '}…voir plus</span> : null}
            </p>
            {cut.truncated ? (
              <p className="nz-sim__hidden">
                {cut.hidden.length} caractères masqués derrière « voir plus » ({Math.round((cut.hidden.length / (cut.hidden.length + cut.visible.length)) * 100)} % du post)
              </p>
            ) : (
              <p className="nz-sim__hidden">Aucune coupure : le post tient en entier.</p>
            )}
          </div>
          <p className="nz-sim__note">
            Coupure estimée : LinkedIn ne publie pas la position exacte. Elle varie avec la largeur d'écran, les
            emojis et le format du post.
          </p>

          <h2 className="nz-sim__summary">
            {problems === 0 && warnings === 0
              ? 'Rien à corriger avant de publier'
              : `${problems} point${problems > 1 ? 's' : ''} à corriger, ${warnings} à surveiller`}
          </h2>
          <ul className="nz-sim__findings">
            {result.findings.map((finding) => (
              <li key={finding.id} className={`nz-sim__finding nz-sim__finding--${finding.severity}`}>
                <span className="nz-sim__badge">{SEVERITY_LABEL[finding.severity]}</span>
                <strong>{finding.title}</strong>
                <p>{finding.detail}</p>
                {finding.fix ? <p className="nz-sim__fix">→ {finding.fix}</p> : null}
                {finding.guide ? <Link to={finding.guide.href}>{finding.guide.label}</Link> : null}
              </li>
            ))}
          </ul>
          <div className="nz-wizard__actions">
            <button type="button" className="nz-btn-secondary" onClick={copyReport}>
              {copied ? 'Copié' : 'Copier le contrôle'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
